import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import {
  InputWithConfirm,
  ApproveDataIcon,
  AddressInputWithConfirm,
  CardContainer,
  CardWithModal,
  uiTypes,
} from 'shared-components';

import { approveAgentSection, saveAgentDetails } from '../../../store/student/actions';
import { useCustomNavigate } from '../../../hooks/useCustomNavigate';
import { useAuth } from '../../../hooks/useAuth';
import QuestionPage from '../../templates/question';
import { useAgentDetailsState, NAME_FIELD_CONFIG, splitAddressLines } from './useAgentDetailsState';
import { useTranslation } from "react-i18next";

type NameFieldId = 'agentFamilyName' | 'agentGivenName';

export default function AgentDetailsPage(): React.ReactElement {
    const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { navigateToStudentDashboard } = useCustomNavigate();
  const { canEditStudents, isAgent } = useAuth();

  const {
    offer,
    displayName,
    isPending,
    isApproved,
    agentApprovedLocally,
    handleApproveAgentLocally,
    nameFields,
    nameApproved,
    nameEdited,
    address,
    draftAddress,
    addressImportApproved,
    addressImportEdited,
    showModal,
    handleApproveNameField,
    handleEditNameField,
    handleUndoNameField,
    handleNameFieldChange,
    handleAddressImportApprove,
    handleAddressImportUndo,
    setDraftAddress,
    openAddressModal,
    closeAddressModal,
    saveAndCloseAddressModal,
    handleRemoveAddress,
    isComplete,
    hasAnyAddressImportValue,
    getAddressCardItems,
  } = useAgentDetailsState();

  function handleSave(): void {
    const { line1, line2, line3 } = splitAddressLines(address.homeAddress);
    const payload: Record<string, string> = {
      agentFamilyName: nameFields.agentFamilyName.trim(),
      agentGivenName: nameFields.agentGivenName.trim(),
      branchAddressLine1: line1.trim(),
      branchAddressLine2: line2.trim(),
      branchAddressLine3: line3.trim(),
      branchCity: address.city.trim(),
      branchCountry: address.country.trim(),
      branchPostcode: address.postCode.trim(),
    };
    dispatch(saveAgentDetails(payload));
    if (agentApprovedLocally) {
      dispatch(approveAgentSection());
    }
  }

  function renderNameField({ id, label }: { id: NameFieldId; label: string }): React.ReactElement {
    const importValue = String(offer[id] ?? '');
    const importEnabled = !!offer[id];
    return (
      <InputWithConfirm
        key={id}
        id={id}
        label={label}
        value={nameFields[id]}
        onChange={(e: React.FormEvent<HTMLInputElement>) => handleNameFieldChange(id, e.currentTarget.value)}
        inputProps={{ autoComplete: 'one-time-code' }}
        importEnabled={importEnabled}
        importValue={importValue}
        importApproved={nameApproved.includes(id)}
        importEdited={nameEdited.includes(id)}
        onImportApprove={() => handleApproveNameField(id)}
        onImportEdit={() => handleEditNameField(id)}
        onImportUndo={() => handleUndoNameField(id)}
      />
    );
  }

  const hasAddressData = getAddressCardItems().some(
    (item: uiTypes.ICardItem) => item.value !== undefined && item.value !== '',
  );

  return (
    <QuestionPage
      section="Agent Details"
      onNext={handleSave}
      onBackToTaskList={navigateToStudentDashboard}
      complete={isComplete() && (isAgent || !isPending || agentApprovedLocally)}
    >
      {!isAgent && (
        <Box mb={10}>
          <Box fontSize="lg" fontWeight="medium" mb={2}>
            {t('common:agentCompanyName')}
                                </Box>
          <Flex gap={{ base: 1, lg: 2 }}>
            <Box
              borderRadius="lg"
              flexGrow="1"
              px="1.125rem"
              py={3}
              fontSize="lg"
              lineHeight={6}
              bgColor="gray.100"
              _dark={{ bgColor: 'gray.800' }}
            >
              {displayName}
            </Box>
            {canEditStudents && isPending && !agentApprovedLocally && (
              <ApproveDataIcon id="agentDisplayName-approve" type="unclicked" onClick={handleApproveAgentLocally} />
            )}
            {(isApproved || agentApprovedLocally) && <ApproveDataIcon id="agentDisplayName-approved" type="clicked" />}
          </Flex>
        </Box>
      )}

      {NAME_FIELD_CONFIG.map(renderNameField)}

      <CardContainer title={t('common:branchAddress')}>
        <CardWithModal
          id="branchAddress"
          index={0}
          modalTitle="Edit Branch Address"
          cardItems={getAddressCardItems()}
          showModal={showModal}
          hasAddMoreButton={false}
          hasAddFirstButton={!hasAddressData ? 'Add Branch Address' : undefined}
          hasInCardActions={!hasAnyAddressImportValue() && hasAddressData}
          hasRemoveCardAction={false}
          toggleModal={() => (showModal ? closeAddressModal() : openAddressModal())}
          onRemove={handleRemoveAddress}
          importEnabled={hasAnyAddressImportValue()}
          importApproved={addressImportApproved}
          importEdited={addressImportEdited}
          onImportApprove={() => handleAddressImportApprove()}
          onImportEdit={() => openAddressModal()}
          canUndo={hasAnyAddressImportValue() && addressImportEdited}
          onImportUndo={() => handleAddressImportUndo()}
        >
          <AddressInputWithConfirm
            id="branchAddress"
            addressLabel="Branch Address"
            addressSubText="e.g. unit 33, 17 Example Street"
            cityLabel="City"
            postcodeLabel="Postcode (if applicable)"
            countryLabel="Country"
            cleanSuggestionsAfterSave={true}
            addressValue={draftAddress.homeAddress}
            cityValue={draftAddress.city}
            postcodeValue={draftAddress.postCode}
            countryValue={draftAddress.country}
            hasSaveButton={true}
            complete={!!draftAddress.homeAddress.trim() && !!draftAddress.city.trim() && !!draftAddress.country.trim()}
            onChangeAddress={(event: React.FormEvent<HTMLTextAreaElement>) => {
              const homeAddress = event.currentTarget.value;
              setDraftAddress(prev => ({ ...prev, homeAddress }));
            }}
            onChangeCity={(event: React.FormEvent<HTMLInputElement>) => {
              const city = event.currentTarget.value;
              setDraftAddress(prev => ({ ...prev, city }));
            }}
            onChangePostcode={(event: React.FormEvent<HTMLInputElement>) => {
              const postCode = event.currentTarget.value;
              setDraftAddress(prev => ({ ...prev, postCode }));
            }}
            onChangeCountry={(value: string) => {
              setDraftAddress(prev => ({ ...prev, country: value }));
            }}
            onSaveAndClose={saveAndCloseAddressModal}
          />
        </CardWithModal>
      </CardContainer>
    </QuestionPage>
  );
}
